import styles from './EmployeeDetailedView.module.css';
import { Box, Button, Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EmployeesService } from 'src/pehra-ts-client';
import { LoadingButton } from '@mui/lab';
import useNotification from 'src/hooks/useNotification';
import { useState } from 'react';
import NewEmployeesModal from '../NewEmployeesModal';
import DeleteConfirmationModal from 'src/components/DeleteConfirmationModal';
import TransferToOtherDistrictModal from 'src/pages/TransferToOtherDistrict/component/TransferToOtherDistrictModal';
import { red } from '@mui/material/colors';
import * as React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';

export interface IEmployeeDetailedView {
  sampleTextProp?: string;
  employeeDetail: any;
  handleOpenTransferModal?: () => void;
}

const modifyLabel = (key: string) => {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

const EmployeeDetailedView: React.FC<IEmployeeDetailedView> = ({ employeeDetail }) => {
  const { id, name, current_office_id, punishments, ...rest } = employeeDetail;
  const [openEditEmployeeModal, setOpenEditEmployeeModal] = useState(false);
  const [openDeleteEmployeeModal, setOpenDeleteEmployeeModal] = useState(false);
  const [openTransferEmployeeModal, setOpenTransferEmployeeModal] = useState(false);

   const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    console.log("Coming in this")
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseEditEmployeeModal = () => {
    setOpenEditEmployeeModal(false);
  };

  const queryClient = useQueryClient();

  const [sendNotification] = useNotification();

  const getEmployeeHistory = async () => {
    try {
      const res = await EmployeesService.getEmployeeHistory(employeeDetail.id);
      return res;
    } catch (error) {
      throw error;
    }
  };

  const fetchEmployeeHistoryQuery = useQuery({
    queryKey: ['employeeHistory', employeeDetail.id],
    queryFn: getEmployeeHistory
  });

  const transferToOffice = async () => {
    try {
      const res = await EmployeesService.moveEmployeeToTransferState({
        employee_ids: [id]
      });
      return res;
    } catch (error) {
      throw error;
    }
  };

  const deleteEmployee = async () => {
    try {
      const res = await EmployeesService.deleteEmployee(id);
    } catch (error) {
      sendNotification({
        msg: 'Employee deletion failed',
        type: 'error'
      });
      throw error;
    } finally {
      sendNotification({
        msg: 'Employee deleted successfully',
        type: 'success'
      });
      setOpenDeleteEmployeeModal(false);
      queryClient.invalidateQueries({
        queryKey: ['employeesList']
      });
    }
  };

  const transferToOfficeMutation = useMutation({
    mutationFn: transferToOffice,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['employeesList']
      });
      sendNotification({
        msg: 'Employee added to transfer pool',
        type: 'success'
      });
    }
  });

  const handleTransferToOffice = () => {
    transferToOfficeMutation.mutate();
  };



  const handleEditEmployeeModal = () => {
    setOpenEditEmployeeModal(true);
  };

  const handleDeleteEmployee = async () => {
    setOpenDeleteEmployeeModal(true);
  };

  const handleTransferEmployee = async () => {
    setOpenTransferEmployeeModal(true);
  };

  // const PunishmentTable = (
  //   <TableContainer component={Paper}>
  //     <Table sx={{ minWidth: 650 }} aria-label="simple table">
  //       <TableHead>
  //         <TableRow sx={{ color: red }}>
  //           <TableCell>Line Hazir</TableCell>
  //           <TableCell align="right">Misconduct</TableCell>
  //           <TableCell align="right">Suspension</TableCell>
  //           <TableCell align="right">14/1</TableCell>
  //         </TableRow>
  //       </TableHead>
  //       <TableBody>
  //         {punishments &&
  //           punishments.map((row: Punishment) => (
  //             <TableRow key={row.line_hazir} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
  //               <TableCell component="th" scope="row">
  //                 {row.line_hazir}
  //               </TableCell>
  //               <TableCell align="right">{row.misconduct}</TableCell>
  //               <TableCell align="right">{row.suspension}</TableCell>
  //               <TableCell align="right">{row.c_14_1}</TableCell>
  //             </TableRow>
  //           ))}
  //       </TableBody>
  //     </Table>
  //   </TableContainer>
  // );

  const EmployeeHistoryTable = (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell>Office Name</TableCell>
            <TableCell align="right">Date Started</TableCell>
            <TableCell align="right">Expected End Date</TableCell>
            <TableCell align="right">Actual End Date</TableCell>

            <TableCell align="right">Transfer Type</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fetchEmployeeHistoryQuery?.data?.offices.map((row) => (
            <TableRow key={row.office_name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell component="th" scope="row">
                {row.office_name}
              </TableCell>
              <TableCell align="right">{row.date_started}</TableCell>
              <TableCell align="right">{row.expected_end_date}</TableCell>
              <TableCell align="right">{row.actual_end_date}</TableCell>

              <TableCell align="right">{row.transfer_type}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Box
        sx={{
          margin: '20px',
          marginLeft: '0',
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <Typography variant="h3" className={styles.title}>
          {modifyLabel(name)}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: '10px'
          }}
        >
          <Button variant="outlined" color="primary" onClick={handleEditEmployeeModal}>
            Edit
          </Button>
          <Button variant="outlined" color="primary" onClick={handleDeleteEmployee}>
            Delete Employee
          </Button>
          <LoadingButton variant="outlined" color="primary" onClick={handleTransferToOffice} loading={transferToOfficeMutation.isPending}>
            Add to Transfer Pool
          </LoadingButton>

          <IconButton color="primary" children={ <MoreVertIcon />} onClick={handleClick}/>


        </Box>
      </Box>
      <Divider />
      <Box className={styles.container}>
        {Object.keys(rest).map((key: string, index: number) => {
          return (
            <Box key={index} className={styles.rowDiv}>
              <Box className={styles.key}>{modifyLabel(key)}</Box>
              <Box className={styles.value}>{employeeDetail[key] || 'N/A'}</Box>
            </Box>
          );
        })}
      </Box>
      <Box>
        <Typography variant="h5" className={styles.title}>
          Employee History
        </Typography>
        {EmployeeHistoryTable}
      </Box>
      <NewEmployeesModal
        openModal={openEditEmployeeModal}
        handleCloseModal={handleCloseEditEmployeeModal}
        data={employeeDetail}
        editMode={true}
      />
      <DeleteConfirmationModal
        openDeleteModal={openDeleteEmployeeModal}
        handleCloseDeleteModal={() => setOpenDeleteEmployeeModal(false)}
        handleDelete={deleteEmployee}
      />

      <TransferToOtherDistrictModal

      openDeleteModal={openTransferEmployeeModal}
      handleCloseDeleteModal={() => setOpenTransferEmployeeModal(false)}
      handleDelete={deleteEmployee}
      data={employeeDetail}

      />

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            borderRadius: '8px', 
            mt: 1,
          },
        }}
      >
        <MenuItem onClick={handleClose}>
           <div className='border-2 border-red'>
          <Button variant="outlined" color="primary" onClick={handleTransferEmployee}>Transfer to Other District</Button>
          </div>
        </MenuItem>

      </Menu>
    </Box>
  );
};

export default EmployeeDetailedView;
